import React, { useState } from "react";
import { Link } from "react-router-dom";
import { FaEnvelope, FaArrowLeft } from "react-icons/fa";
import { API_BASE_URL } from "../environment";
import { API } from "../common/Constants";
import "../styles/verification.css";

const ForgotPassword = () => {
  const [formData, setFormData] = useState({
    email: "",
  });

  const [formErrors, setFormErrors] = useState({});
  const [formStatus, setFormStatus] = useState({
    submitting: false,
    submitted: false,
    error: false,
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));

    // Clear error for this field when user types
    if (formErrors[name]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.email.trim()) {
      errors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = "Email is invalid";
    }

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setFormStatus((prev) => ({
      ...prev,
      submitting: true,
      submitted: false,
      error: false,
    }));

    try {
      const response = await fetch(`${API_BASE_URL}${API.FORGOT_PASSWORD}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: formData.email,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Password reset request failed");
      }

      setFormStatus((prev) => ({
        ...prev,
        submitting: false,
        submitted: true,
        error: false,
      }));
    } catch (error) {
      console.error("Forgot password error:", error);
      setFormStatus((prev) => ({
        ...prev,
        submitting: false,
        error: true,
        errorMessage:
          error.message || "Password reset request failed. Please try again.",
      }));
    }
  };

  return (
    <section className="login-page section-padding">
      <div className="container">
        <div className="section-header">
          <h2>Forgot Password</h2>
          <p>Enter your email address to reset your password</p>
        </div>

        <div className="login-content">
          <div className="login-form-container">
            <Link to="/login" className="back-link">
              <FaArrowLeft /> Back to Login
            </Link>

            {formStatus.submitted ? (
              <div className="success-message">
                <h3>Password Reset Link Sent!</h3>
                <p>
                  We've sent a password reset link to your email address. Please
                  check your inbox and follow the instructions to reset your
                  password.
                </p>
                <div className="mt-4">
                  <Link to="/login" className="btn btn-primary">
                    Return to Login
                  </Link>
                </div>
              </div>
            ) : (
              <form className="login-form" onSubmit={handleSubmit}>
                {formStatus.error && (
                  <div className="error-alert">
                    <p>
                      {formStatus.errorMessage ||
                        "Password reset request failed. Please try again."}
                    </p>
                  </div>
                )}
                <div className="form-group">
                  <label htmlFor="email">
                    <FaEnvelope /> Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={formErrors.email ? "error" : ""}
                  />
                  {formErrors.email && (
                    <span className="error-message">{formErrors.email}</span>
                  )}
                </div>

                <button
                  type="submit"
                  className="btn btn-primary login-btn"
                  disabled={formStatus.submitting}
                >
                  {formStatus.submitting
                    ? "Sending Reset Link..."
                    : "Send Reset Link"}
                </button>

                <div className="login-link mt-4 text-center">
                  Remembered your password?{" "}
                  <Link to="/login" className="text-link">
                    Back to Login
                  </Link>
                </div>
              </form>
            )}
          </div>

          <div className="login-info">
            <div className="login-info-content">
              <h3>Password Recovery</h3>
              <div className="login-info-icon">
                <FaEnvelope />
              </div>
              <p className="login-info-description">
                We'll send a secure link to your email address to reset your
                password. Make sure you use the email address associated with
                your account.
              </p>
              <ul className="login-benefits">
                <li>
                  <span className="benefit-dot"></span>
                  Check your inbox for the reset link
                </li>
                <li>
                  <span className="benefit-dot"></span>
                  The link will expire after 24 hours
                </li>
                <li>
                  <span className="benefit-dot"></span>
                  Create a strong, unique password
                </li>
                <li>
                  <span className="benefit-dot"></span>
                  Keep your new password secure
                </li>
              </ul>
              <div className="login-info-footer">
                <p>Need assistance?</p>
                <Link to="/#contact" className="support-link">
                  Contact Support
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ForgotPassword;
